import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

// Extend dayjs with relative time plugin
dayjs.extend(relativeTime);

/**
 * Format utilities composable - replaces FormatMixin
 */
export function useFormat() {
  /**
   * Capitalize each word in a string
   * @param {string} value - The string to capitalize
   * @returns {string} - The capitalized string
   */
  const capitalize = (value) => {
    if (!value) return '';
    
    return value
      .split(' ')
      .map(val => val.charAt(0).toUpperCase() + val.substr(1).toLowerCase())
      .join(' ');
  };

  /**
   * Format money from cents to dollars (legacy compatibility)
   * @param {number} value - The value in cents
   * @returns {string} - The formatted money string
   */
  const formatMoney = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '$0.00';
    }
    
    value /= 100;
    return `$${value.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,')}`;
  };

  /**
   * Format a number as currency
   * @param {number} value - The value to format
   * @param {string} currency - The currency code (default: 'USD')
   * @returns {string} - The formatted currency string
   */
  const formatCurrency = (value, currency = 'USD') => {
    if (value === null || value === undefined || isNaN(value)) {
      return '$0.00';
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(value);
  };

  /**
   * Format a date using dayjs (legacy compatibility)
   * @param {Date|string} date - The date to format
   * @param {string} format - The format string (default: 'MMMM D, YYYY')
   * @returns {string} - The formatted date string
   */
  const formatDate = (date, format = 'MMMM D, YYYY') => {
    if (!date) return '';
    return dayjs(date).format(format);
  };

  /**
   * Format a date in short format (MM/DD/YYYY)
   * @param {Date|string} date - The date to format
   * @returns {string} - The formatted date string
   */
  const formatShortDate = (date) => {
    if (!date) return '';
    return dayjs(date).format('MM/DD/YYYY');
  };

  /**
   * Format a date with time in short format (MM/DD/YYYY hh:mma)
   * @param {Date|string} date - The date to format
   * @returns {string} - The formatted date time string
   */
  const formatShortDateTime = (date) => {
    if (!date) return '';
    return dayjs(date).format('MM/DD/YYYY hh:mma');
  };

  /**
   * Format a date as relative time (e.g., "2 days ago")
   * @param {Date|string} date - The date to format
   * @returns {string} - The relative time string
   */
  const formatDateAgo = (date) => {
    if (!date) return '';
    return dayjs(date).from(dayjs());
  };

  /**
   * Format a date with flexible options (new Vue 3 implementation)
   * @param {Date|string} date - The date to format
   * @param {object} options - Intl.DateTimeFormat options
   * @returns {string} - The formatted date string
   */
  const formatDateWithOptions = (date, options = {}) => {
    if (!date) return '';
    
    const dateObj = date instanceof Date ? date : new Date(date);
    
    const defaultOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    
    return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(dateObj);
  };

  /**
   * Format a number with thousands separators
   * @param {number} value - The value to format
   * @returns {string} - The formatted number string
   */
  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0';
    }
    
    return new Intl.NumberFormat('en-US').format(value);
  };

  /**
   * Truncate text to a specified length (with HTML stripping for legacy compatibility)
   * @param {string} text - The text to truncate
   * @param {number} length - The maximum length
   * @param {string} clamp - The suffix to append (default: '...')
   * @returns {string} - The truncated text
   */
  const truncate = (text, length, clamp = '...') => {
    if (!text) return '';
    
    // Strip HTML tags like the original implementation
    const node = document.createElement('div');
    node.innerHTML = text;
    const content = node.textContent;
    
    return content.length > length ? content.slice(0, length) + clamp : content;
  };

  /**
   * Truncate text to a specified length (plain text version)
   * @param {string} text - The text to truncate
   * @param {number} length - The maximum length
   * @param {string} suffix - The suffix to append (default: '...')
   * @returns {string} - The truncated text
   */
  const truncateText = (text, length, suffix = '...') => {
    if (!text || text.length <= length) {
      return text || '';
    }
    
    return text.substring(0, length) + suffix;
  };

  return {
    // Legacy compatibility functions (matching original FormatMixin)
    capitalize,
    formatMoney,
    formatDate,
    formatShortDate,
    formatShortDateTime,
    formatDateAgo,
    truncate,
    
    // New Vue 3 implementations
    formatCurrency,
    formatDateWithOptions,
    formatNumber,
    truncateText
  };
}
